import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { msalConfig } from './lib/authConfig.js';
import { ToastProvider } from './hooks/useToast.jsx';
import { ThemeProvider } from './hooks/useTheme.jsx';
import { CurrentUserProvider } from './hooks/useCurrentUser.jsx';
import { UnsavedWorkProvider } from './hooks/useUnsavedWork.jsx';
import App from './App.jsx';
import ExpiryNotificationsListener from './components/ExpiryNotificationsListener.jsx';
import AppStatusBanner from './components/AppStatusBanner.jsx';
import ErrorBoundary from './components/ErrorBoundary.jsx';
import './styles/global.css';

// ── Canonical-domain guard ──
// Approval emails historically linked to the raw SWA default hostname
// (…azurestaticapps.net). MSAL's redirectUri is the current origin, so a
// user who lands there is stranded on a hostname that isn't a registered
// Entra redirect URI — sign-in fails and they're stuck on a broken origin.
// When a canonical app URL is configured at build time (VITE_APP_BASE_URL),
// bounce any azurestaticapps.net load to it, preserving the deep-link path,
// so even emails already sent to the old hostname work. Runs before MSAL
// init, so there's no failed auth attempt on the wrong origin. Loop-safe:
// the canonical host is the custom domain, never an azurestaticapps.net one.
// NOTE: this bounces ANY *.azurestaticapps.net host — fine while deploys go
// straight to main. If PR preview environments are introduced later,
// tighten this to the exact legacy production hostname so previews aren't
// bounced to prod.
function redirectToCanonicalIfNeeded() {
  const canonical = import.meta.env.VITE_APP_BASE_URL;
  if (!canonical) return false;
  let canonicalHost;
  try { canonicalHost = new URL(canonical).host; } catch { return false; }
  const here = window.location.host;
  if (here === canonicalHost) return false;
  if (!/\.azurestaticapps\.net$/i.test(here)) return false;
  const target = canonical.replace(/\/+$/, '') +
    window.location.pathname + window.location.search + window.location.hash;
  window.location.replace(target);
  return true;
}

async function bootstrap(msalInstance) {
  await msalInstance.initialize();

  try {
    const response = await msalInstance.handleRedirectPromise();
    if (response?.account) {
      msalInstance.setActiveAccount(response.account);
    } else if (
      msalInstance.getAllAccounts().length > 0 &&
      !msalInstance.getActiveAccount()
    ) {
      msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
    }
  } catch (err) {
    console.error('MSAL redirect handling failed', err);
  }

  ReactDOM.createRoot(document.getElementById('root')).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <ThemeProvider>
        <ToastProvider>
          <BrowserRouter>
            <UnsavedWorkProvider>
              <CurrentUserProvider>
                <ExpiryNotificationsListener />
                <AppStatusBanner />
                <ErrorBoundary>
                  <App />
                </ErrorBoundary>
              </CurrentUserProvider>
            </UnsavedWorkProvider>
          </BrowserRouter>
        </ToastProvider>
        </ThemeProvider>
      </MsalProvider>
    </React.StrictMode>
  );
}

// Bounce off the broken hostname first; only boot the app if we're staying.
if (!redirectToCanonicalIfNeeded()) {
  const msalInstance = new PublicClientApplication(msalConfig);

  // Expose for api.js to acquire tokens
  window.__msalInstance = msalInstance;

  if (
    !msalInstance.getActiveAccount() &&
    msalInstance.getAllAccounts().length > 0
  ) {
    msalInstance.setActiveAccount(msalInstance.getAllAccounts()[0]);
  }

  msalInstance.addEventCallback((event) => {
    if (
      event.eventType === EventType.LOGIN_SUCCESS &&
      event.payload &&
      event.payload.account
    ) {
      msalInstance.setActiveAccount(event.payload.account);
    }
  });

  bootstrap(msalInstance);
}
